/**
 * Tâche 1400 (Eric 2026-09-30) — « Diagnostic parlant ».
 *
 * Quand le pipeline échoue, l'écran affichait la trace brute du programme :
 * quarante lignes de configuration ffmpeg, un code HTTP, ou un nom de classe
 * Python. Personne ne peut en déduire QUOI FAIRE — et surtout pas si le
 * problème vient du fichier, de la clé du fournisseur ou de l'hébergeur.
 *
 * Trois décisions de conception :
 *
 *   1. **La traduction se fait à l'affichage, pas à l'écriture.** Les échecs
 *      déjà en base portent leur trace brute : un interprète côté serveur au
 *      moment de l'échec ne dirait rien des centaines de lignes existantes.
 *      Ici, une vidéo tombée en panne il y a six mois reçoit aussi son
 *      diagnostic.
 *
 *   2. **La trace brute n'est JAMAIS jetée.** Elle reste sous « Détails
 *      techniques » : c'est elle qu'on transmet quand la cause n'est pas
 *      reconnue, et un diagnostic qui remplacerait la preuve rendrait le
 *      dépannage impossible.
 *
 *   3. **Une cause non reconnue le DIT.** Deviner (« probablement le codec »)
 *      enverrait réexporter une vidéo qui n'a rien. Le repli nomme l'étape,
 *      admet ne pas savoir, et renvoie à la trace.
 *
 * Les deux langues vivent sur la même entrée (motif 1324b) : ce catalogue vit
 * hors de tout composant, il ne peut donc pas appeler le traducteur.
 */

// L'ordre COMPTE : la première signature qui correspond gagne. Les causes
// précises passent donc avant les familles larges (un « 404 » dans une trace
// de stockage n'est pas la même chose qu'un 404 d'API).
const SIGNATURES = [
  {
    id: "arret-manuel",
    motif: /arr[eê]t forc[eé]|pipelinecancelled|annul[eé] par l'utilisateur|\bcancelled\b/i,
    titre: "Traitement interrompu volontairement",
    titre_en: "Processing stopped on purpose",
    cause: "Quelqu'un a demandé l'arrêt, ou le lot a été annulé. Ce n'est pas une panne.",
    cause_en: "Someone requested the stop, or the batch was cancelled. This is not a failure.",
    action: "Relancer le pipeline quand la vidéo doit reprendre.",
    action_en: "Relaunch the pipeline when the video should resume.",
  },
  {
    id: "arret-automatique",
    motif: /arr[eê]t automatique/i,
    titre: "Arrêt automatique après plusieurs échecs de suite",
    titre_en: "Automatic stop after repeated failures",
    cause: "La même étape a échoué plusieurs fois ; le pipeline s'est arrêté pour ne pas consommer des crédits en boucle.",
    cause_en: "The same step failed several times; the pipeline stopped so it would not burn credits in a loop.",
    action: "Lire la cause de l'échec précédent dans le journal des étapes AVANT de relancer.",
    action_en: "Read the previous failure in the step log BEFORE relaunching.",
  },
  {
    id: "fichier-absent",
    motif: /nosuchkey|object not found|no_storage_path|no such file or directory|fichier introuvable/i,
    titre: "Le fichier vidéo est introuvable dans le stockage",
    titre_en: "The video file cannot be found in storage",
    cause: "Le stockage ne retrouve pas l'objet : l'envoi a été coupé avant la fin, ou le fichier a été supprimé après coup.",
    cause_en: "Storage cannot find the object: the upload was cut short, or the file was deleted afterwards.",
    action: "Vérifier l'état du fichier, puis le téléverser de nouveau.",
    action_en: "Check the file status, then upload it again.",
  },
  {
    id: "fichier-abime",
    motif: /moov atom not found|invalid data found when processing input|unexpected end of file|premature end/i,
    titre: "Le fichier vidéo est incomplet ou abîmé",
    titre_en: "The video file is incomplete or damaged",
    cause: "L'index du fichier manque, ou les données s'arrêtent avant la fin : envoi interrompu, ou export d'origine raté.",
    cause_en: "The file index is missing, or the data stops early: interrupted upload, or a failed original export.",
    action: "Réexporter la vidéo depuis la source, puis la téléverser de nouveau.",
    action_en: "Re-export the video from the source, then upload it again.",
  },
  {
    id: "codec-refuse",
    motif: /unknown encoder|unknown decoder|decoder \(codec|codec not currently supported|unsupported codec|no decoder for|invalid argument.*pixel format/i,
    titre: "Le codec de cette vidéo n'est pas pris en charge",
    titre_en: "This video codec is not supported",
    cause: "Le convertisseur ne sait pas lire la piste de ce fichier. C'est fréquent avec les captures d'écran et les formats propriétaires.",
    cause_en: "The converter cannot read this file's track. Common with screen captures and proprietary formats.",
    action: "Convertir la vidéo en MP4 (H.264 + AAC), puis la téléverser de nouveau.",
    action_en: "Convert the video to MP4 (H.264 + AAC), then upload it again.",
  },
  {
    id: "sans-piste-audio",
    motif: /does not contain any stream|matches no streams|no audio stream|audio: none|stream map .* matches no/i,
    titre: "Cette vidéo ne contient aucune piste audio",
    titre_en: "This video has no audio track",
    cause: "Le fichier n'a pas de son : il n'y a rien à transcrire.",
    cause_en: "The file has no sound: there is nothing to transcribe.",
    action: "Vérifier l'export d'origine — la piste audio a très probablement été omise.",
    action_en: "Check the original export — the audio track was most likely left out.",
  },
  {
    id: "audio-vide",
    motif: /extraction audio invalide|fichier audio trop petit|audio trop court/i,
    titre: "L'audio extrait est vide ou trop court",
    titre_en: "The extracted audio is empty or too short",
    cause: "L'extraction a produit un fichier quasi muet : piste silencieuse, ou durée presque nulle.",
    cause_en: "Extraction produced an almost silent file: silent track, or near-zero duration.",
    action: "Écouter la vidéo d'origine pour confirmer qu'elle a du son, puis relancer.",
    action_en: "Play the original video to confirm it has sound, then relaunch.",
  },
  {
    id: "disque-plein",
    motif: /no space left on device|disk quota exceeded|espace disque/i,
    titre: "Le disque de traitement est plein",
    titre_en: "The processing disk is full",
    cause: "La machine de traitement n'a plus de place pour écrire le fichier audio intermédiaire.",
    cause_en: "The processing machine has no room left to write the intermediate audio file.",
    action: "Prévenir l'équipe Knoria : c'est une panne d'hébergement, relancer n'y changera rien.",
    action_en: "Notify the Knoria team: this is a hosting failure, relaunching will not help.",
  },
  {
    id: "memoire-insuffisante",
    motif: /cannot allocate memory|out of memory|memoryerror|\bkilled\b|signal 9|\boom\b/i,
    titre: "Le traitement a manqué de mémoire",
    titre_en: "Processing ran out of memory",
    cause: "Le fichier est trop volumineux pour la machine de traitement, qui a été arrêtée en cours de route.",
    cause_en: "The file is too large for the processing machine, which was killed mid-run.",
    action: "Découper la vidéo en parties plus courtes, ou prévenir l'équipe Knoria.",
    action_en: "Split the video into shorter parts, or notify the Knoria team.",
  },
  {
    id: "droits-refuses",
    motif: /permission denied|\beacces\b|operation not permitted/i,
    titre: "Droits d'accès refusés sur le fichier de travail",
    titre_en: "Access denied on the working file",
    cause: "Le traitement n'a pas pu lire ou écrire son fichier temporaire.",
    cause_en: "Processing could not read or write its temporary file.",
    action: "Prévenir l'équipe Knoria : c'est une panne d'hébergement, pas un problème de vidéo.",
    action_en: "Notify the Knoria team: this is a hosting failure, not a video problem.",
  },
  {
    id: "cle-refusee",
    motif: /invalid api key|incorrect api key|invalid_api_key|authentication_error|unauthorized|\b401\b|\b403\b/i,
    titre: "La clé IA a été refusée par le fournisseur",
    titre_en: "The AI key was rejected by the provider",
    cause: "Le fournisseur refuse la clé du Forfait AI : elle est expirée, révoquée, ou mal recopiée.",
    cause_en: "The provider rejects the AI Package key: expired, revoked, or mistyped.",
    action: "Tester la clé dans la page Forfait AI, puis la remplacer si le test échoue.",
    action_en: "Test the key on the AI Package page, then replace it if the test fails.",
  },
  {
    id: "credits-epuises",
    motif: /\b429\b|rate limit|insufficient_quota|insufficient funds|credit balance|quota exceeded|too many requests/i,
    titre: "Crédits ou quota épuisés chez le fournisseur",
    titre_en: "Provider credits or quota exhausted",
    cause: "Le fournisseur refuse de nouveaux appels : le solde est à zéro, ou la limite de débit est atteinte.",
    cause_en: "The provider refuses new calls: balance is empty, or the rate limit is reached.",
    action: "Recharger le compte du fournisseur, ou basculer sur une autre clé dans le Forfait AI.",
    action_en: "Top up the provider account, or switch to another key in the AI Package.",
  },
  {
    id: "delai-depasse",
    motif: /timeout|timed out|deadline exceeded|d[eé]lai d[eé]pass[eé]/i,
    titre: "Le délai de l'étape a été dépassé",
    titre_en: "The step exceeded its time limit",
    cause: "L'étape n'a rien rendu dans le temps imparti : fichier très long, ou fournisseur très lent.",
    cause_en: "The step returned nothing within the allotted time: very long file, or a very slow provider.",
    action: "Relancer. Si cela se reproduit, découper la vidéo en parties plus courtes.",
    action_en: "Relaunch. If it happens again, split the video into shorter parts.",
  },
  {
    id: "fournisseur-en-panne",
    motif: /internal server error|bad gateway|service unavailable|overloaded|\b50[0234]\b/i,
    titre: "Le fournisseur est en panne passagère",
    titre_en: "The provider is temporarily down",
    cause: "Le service distant a répondu par une erreur de son côté. Rien n'est en cause dans la vidéo ni dans la configuration.",
    cause_en: "The remote service answered with an error of its own. Nothing is wrong with the video or the setup.",
    action: "Attendre quelques minutes, puis relancer.",
    action_en: "Wait a few minutes, then relaunch.",
  },
  {
    id: "reseau",
    motif: /connection (error|reset|refused|aborted)|econnreset|getaddrinfo|name resolution|ssl(error|certificate)|network is unreachable/i,
    titre: "La connexion au fournisseur a échoué",
    titre_en: "The connection to the provider failed",
    cause: "L'appel n'a pas abouti au réseau : coupure passagère, ou service inaccessible depuis l'hébergeur.",
    cause_en: "The call never reached the network: a brief outage, or a service unreachable from the host.",
    action: "Relancer. Si l'échec persiste, prévenir l'équipe Knoria.",
    action_en: "Relaunch. If it keeps failing, notify the Knoria team.",
  },
  {
    id: "transcription-absente",
    motif: /transcription introuvable|transcript not found|aucune transcription/i,
    titre: "Aucune transcription à exploiter",
    titre_en: "No transcript to work from",
    cause: "L'extraction des connaissances a démarré alors que la transcription n'existe pas.",
    cause_en: "Knowledge extraction started while the transcript does not exist.",
    action: "Relancer la transcription, puis seulement ensuite l'extraction des connaissances.",
    action_en: "Relaunch the transcript first, and only then the knowledge extraction.",
  },
  {
    id: "locuteur-unique",
    motif: /only one speaker|un seul locuteur|speaker_labels.*fail|diarization failed/i,
    titre: "La diarisation n'a pas pu séparer les voix",
    titre_en: "Diarization could not separate the voices",
    cause: "Le service n'a distingué qu'une seule voix : enregistrement saturé, voix trop proches, ou audio mono très compressé.",
    cause_en: "The service heard a single voice: clipped recording, voices too similar, or heavily compressed mono audio.",
    action: "Reprendre avec un seul locuteur si la vidéo n'en compte qu'un, sinon renommer les tours à la main depuis la légende.",
    action_en: "Resume with one speaker if the video truly has one, otherwise rename turns by hand from the legend.",
  },
];

/**
 * @param {Object} video — au moins `{ error, pipeline_stage }`.
 * @returns {{id: string, reconnu: boolean, titre: string, titre_en: string,
 *            cause: string, cause_en: string, action: string,
 *            action_en: string, technique: string, etape: string}}
 *          `reconnu: false` signifie « on ne sait pas » : l'appelant DOIT
 *          alors nommer l'étape et renvoyer à `technique`, jamais inventer
 *          une cause.
 */
function diagnostiquerEchec(video) {
  const brut = String((video && (video.error || video.pipeline_error)) || "").trim();
  const etape = String((video && video.pipeline_stage) || "").trim();

  for (const sig of SIGNATURES) {
    if (sig.motif.test(brut)) {
      return {
        id: sig.id,
        reconnu: true,
        titre: sig.titre,
        titre_en: sig.titre_en,
        cause: sig.cause,
        cause_en: sig.cause_en,
        action: sig.action,
        action_en: sig.action_en,
        technique: brut,
        etape,
      };
    }
  }

  return {
    id: "inconnu",
    reconnu: false,
    titre: "Cause non reconnue",
    titre_en: "Cause not recognised",
    cause: "Le message ci-dessous n'est pas dans le catalogue des causes connues.",
    cause_en: "The message below is not in the catalogue of known causes.",
    action: "Relancer une fois. Si le même message revient, le transmettre tel quel à l'équipe Knoria.",
    action_en: "Relaunch once. If the same message comes back, forward it as-is to the Knoria team.",
    technique: brut,
    etape,
  };
}

/** Titre court, dans la langue en cours — pour une ligne de liste. */
function titreEchec(video, lang) {
  const d = diagnostiquerEchec(video);
  return lang === "en" ? d.titre_en : d.titre;
}

module.exports = {
  SIGNATURES,
  diagnostiquerEchec,
  titreEchec,
};
